import { useEffect } from 'react';
import {
  armOverlayGuard,
  consumeOverlayGuard,
  disarmOverlayGuard,
  dismissOpenOverlay,
  findOpenOverlay,
  isOverlayGuardArmed,
} from '../utils/overlayBack';

/**
 * AndroidBackGuard - sistemsko „nazad" na Androidu prvo zatvara otvoren panel.
 *
 * Montira se jednom (u Shell-u) i ništa ne renderuje.
 *
 * Kada je otvoren desni side bar / modal (bilo koji overlay preko celog
 * ekrana), u istoriju se ubaci dodatni unos sa istom adresom. Tada sistemsko
 * „nazad" (dugme/gest) samo potroši taj unos: adresa ostaje nepromenjena, pa
 * react-router ne menja stranicu - a mi u tom trenutku zatvorimo panel. Kada
 * ništa nije otvoreno, „nazad" radi normalno (prethodna stranica).
 *
 * @see ../utils/overlayBack.js
 */
const AndroidBackGuard = () => {
  useEffect(() => {
    let frame = 0;
    let timer = 0;

    // Usaglasi istorijski „štit" sa stvarnim stanjem na ekranu.
    const sync = () => {
      frame = 0;
      window.clearTimeout(timer);
      timer = 0;
      if (findOpenOverlay()) armOverlayGuard();
      else disarmOverlayGuard();
    };

    // Paneli se otvaraju/zatvaraju klikom, pa posle svake interakcije proverimo
    // stanje. `requestAnimationFrame` pokriva redovan slučaj (react je već
    // upisao izmene u DOM), a `setTimeout` i slučaj kada overlay stigne malo
    // kasnije.
    const scheduleSync = (delay = 80) => {
      if (!frame) frame = window.requestAnimationFrame(sync);
      window.clearTimeout(timer);
      timer = window.setTimeout(sync, delay);
    };

    // Klik bilo gde može da otvori ili zatvori panel.
    const onDocumentClick = () => scheduleSync();

    const onPopState = () => {
      // Ništa nije bilo otvoreno - obično „nazad" kroz stranice.
      if (!isOverlayGuardArmed()) return;
      consumeOverlayGuard();
      if (dismissOpenOverlay()) {
        // Proveri posle animacije zatvaranja da li je ispod ostao još neki
        // otvoren panel; ako jeste, naoružaj štit i za njega.
        scheduleSync(450);
      }
    };

    document.addEventListener('click', onDocumentClick, true);
    window.addEventListener('popstate', onPopState);
    sync();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      document.removeEventListener('click', onDocumentClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  return null;
};

export default AndroidBackGuard;
